/**
 * The lifecycles, as data tables. The single source of truth
 * (`.docs/00-index.md` §5): "no `if (status === …) status = …` in a service".
 *
 * `ARCHITECTURE.md` §Conventions: "Lifecycles from SERVICES.md and PAYMENTS.md are
 * TypeScript unions with a `transition(from, event)` table in `packages/shared`.
 * The database has a `CHECK` on the enum; the service refuses illegal transitions."
 *
 * A table is right when reading it is reading `SERVICES.md`. Every row below carries
 * the sentence it came from, and a row with no sentence behind it is not here.
 */
import { DomainError } from './errors.js';
export function defineMachine(spec) {
    const states = Object.keys(spec.table);
    if (!states.includes(spec.initial)) {
        throw new DomainError('ValidationFailed', `${spec.entity}: "${spec.initial}" is the initial state but not a state.`, { entity: spec.entity });
    }
    const events = new Set();
    for (const state of states) {
        const row = spec.table[state];
        for (const event of Object.keys(row)) {
            // `Object.keys` only yields keys the row has, so the `Partial` cannot bite here;
            // and a row that did carry an explicit `undefined` falls into the throw below,
            // which is the right answer anyway.
            const target = row[event];
            if (!states.includes(target)) {
                throw new DomainError('ValidationFailed', `${spec.entity}: ${state} + ${event} leads to "${target}", which is not a state.`, { entity: spec.entity });
            }
            events.add(event);
        }
    }
    /** `from` may be a string a database row lied about, so the row may be missing. */
    function lookup(from, event) {
        const row = spec.table[from];
        return row?.[event];
    }
    return {
        entity: spec.entity,
        initial: spec.initial,
        states,
        events: [...events].sort((a, b) => a.localeCompare(b)),
        transition(from, event) {
            const to = lookup(from, event);
            if (to === undefined) {
                throw new DomainError('IllegalTransition', `A ${spec.entity} cannot ${event} from ${from}.`, { entity: spec.entity, from, event });
            }
            return to;
        },
        can(from, event) {
            return lookup(from, event) !== undefined;
        },
        eventsFrom(from) {
            const row = spec.table[from];
            return Object.keys(row ?? {});
        },
        isTerminal(state) {
            const row = spec.table[state];
            return Object.keys(row ?? {}).length === 0;
        },
    };
}
/**
 * `SERVICES.md` §Retainers: "States: `active → past_due → active | paused |
 * cancelled`, plus `ending`." · "Failed charge: retainer goes to `past_due`,
 * retries per PAYMENTS.md. Work pauses after 14 days past due." · "Cancellation
 * takes effect at period end… Retainer goes to `ending` when cancellation is
 * scheduled."
 */
export const retainerMachine = defineMachine({
    entity: 'retainer',
    initial: 'active',
    table: {
        active: {
            charge_failed: 'past_due',
            pause: 'paused',
            schedule_cancellation: 'ending',
            cancel_immediately: 'cancelled',
        },
        past_due: {
            // A retry that fails again leaves it past due (PAYMENTS.md §Operations).
            charge_failed: 'past_due',
            charge_recovered: 'active',
            pause: 'paused',
            schedule_cancellation: 'ending',
            cancel_immediately: 'cancelled',
        },
        paused: {
            // No `charge_recovered` here. `.docs/12-retainers.md` frames recovery and resuming
            // as two steps ("resume while past_due -> refused (recover first)"), one state
            // carries both the voluntary pause and the 14-days-past-due pause, and no document
            // says money arriving un-pauses a client who asked to be paused. A `markRecovered`
            // on a paused retainer is an IllegalTransition, which is the loud answer.
            resume: 'active',
            schedule_cancellation: 'ending',
            cancel_immediately: 'cancelled',
        },
        ending: {
            period_ended: 'cancelled',
            cancel_immediately: 'cancelled',
        },
        cancelled: {},
    },
});
/**
 * `SERVICES.md` §Extras: "Work order states: `draft → proposed → approved →
 * in_progress → delivered → billed`. `declined` and `cancelled` are terminal."
 *
 * `proposed + propose` is a re-proposal: `.docs/00-index.md` §6 says an expired
 * quote cannot be approved and "staff re-propose". Expiry itself is not a
 * transition — the quote stays `proposed` and the service refuses `approve` with
 * `QuoteExpired`.
 *
 * `proposed + revise → draft` is session 11's, and `.docs/11-work-orders.md` §Design
 * decisions is the sentence it comes from: "A quote's price is snapshotted at
 * `price()`; re-pricing after `propose` is a new proposal (`draft` again via
 * `update`, then `propose` again) — **never a silent edit**." Without the row there
 * is no way back to the only state a price may be written in, and the alternative
 * would be editing a number a client is looking at. `contentPlanMachine` below has
 * the same edge for the same reason.
 *
 * There is no `cancel` from `delivered`: work that has been delivered is billed.
 */
export const workOrderMachine = defineMachine({
    entity: 'work order',
    initial: 'draft',
    table: {
        draft: { propose: 'proposed', cancel: 'cancelled' },
        proposed: {
            propose: 'proposed',
            revise: 'draft',
            approve: 'approved',
            decline: 'declined',
            cancel: 'cancelled',
        },
        approved: { start: 'in_progress', cancel: 'cancelled' },
        in_progress: { deliver: 'delivered', cancel: 'cancelled' },
        delivered: { bill: 'billed' },
        billed: {},
        declined: {},
        cancelled: {},
    },
});
/**
 * `PAYMENTS.md`: "Invoice states: `draft → sent → processing → paid | failed`."
 *
 * `sent + charge_succeeded` and `failed + charge_succeeded` are the Wise path: a
 * transfer lands against an invoice we never charged, or against one whose direct
 * debit already failed — which is exactly the Vela row in the review queue on
 * `studio/money.html`. `processing` covers the days a bank debit is in flight.
 */
export const invoiceMachine = defineMachine({
    entity: 'invoice',
    initial: 'draft',
    table: {
        draft: { issue: 'sent' },
        sent: { charge_started: 'processing', charge_succeeded: 'paid' },
        processing: { charge_succeeded: 'paid', charge_failed: 'failed' },
        paid: {},
        failed: { retry: 'processing', charge_succeeded: 'paid' },
    },
});
/**
 * `ARCHITECTURE.md` §The task queue: "States: `open → claimed → in_progress →
 * blocked | review | done`, with `cancelled` terminal." · "`tasks_complete` on a
 * task with `needs_review` moves it to `review`" — so the service picks
 * `submit_for_review` or `complete`, and both are here. · "`tasks.releaseStaleLeases`
 * returns abandoned ones", which is `lease_expired` from the two leased states.
 */
export const taskMachine = defineMachine({
    entity: 'task',
    initial: 'open',
    table: {
        open: { claim: 'claimed', cancel: 'cancelled' },
        claimed: { start: 'in_progress', release: 'open', lease_expired: 'open', cancel: 'cancelled' },
        in_progress: {
            block: 'blocked',
            submit_for_review: 'review',
            complete: 'done',
            release: 'open',
            lease_expired: 'open',
            cancel: 'cancelled',
        },
        blocked: { unblock: 'in_progress', cancel: 'cancelled' },
        review: { approve_review: 'done', cancel: 'cancelled' },
        done: {},
        cancelled: {},
    },
});
/**
 * The states a task is somebody's in. `task_held_while_working` (`apps/api`
 * `modules/tasks/schema.ts`) is the same three, as the constraint that refuses one of
 * them with nobody holding it.
 */
export const TASK_HELD_STATES = new Set([
    'claimed',
    'in_progress',
    'blocked',
]);
/**
 * Whether a task in this state can be handed to **nobody**.
 *
 * A held task can only when the machine can put it back on the queue: `claimed` and
 * `in_progress` release to `open`, and `blocked` has no such move — it is waiting on
 * somebody, and that wait belongs to whoever is holding it. And a finished task is
 * not handed to anybody, nobody included: `done` stays with whoever did the work.
 * One answer for the service that refuses the hand-over (`tasks.assign`) and the
 * pickers that used to offer "Unassigned" on a blocked task and have it refused
 * every time.
 */
export function taskCanBeUnassigned(status) {
    if (taskMachine.isTerminal(status))
        return false;
    return !TASK_HELD_STATES.has(status) || taskMachine.can(status, 'release');
}
/**
 * `SERVICES.md` §Usage. Neither state is terminal, and `limit_reached` is not one
 * of them: it is what the ingestion API answers past the cap, not a stored status.
 */
export const widgetMachine = defineMachine({
    entity: 'widget',
    initial: 'active',
    table: {
        active: { pause: 'paused' },
        paused: { resume: 'active' },
    },
});
/**
 * `ARCHITECTURE.md` §Modules: `notifications` owns `email_outbox` — "every mail the
 * system sends, before and after sending". The event names match the states they
 * produce, which is why they read as past tense.
 *
 * `skipped` is session 06's. `.docs/06-jobs-mail-storage.md` §Edge cases asks for a
 * status that means "we decided not to send this" rather than looping. It is
 * terminal, and deliberately not `failed`: nothing went wrong, and a `failed` row is
 * one the drain job retries. The row still exists, because "we decided not to send
 * this" is the answer somebody asking "why did the client not get the digest" needs.
 *
 * It is reachable from **both** `queued` and `failed`. A mail the provider refused is
 * one the retry picks up, and the reason not to send it at all — the client was
 * archived — can arrive in between.
 */
export const emailOutboxMachine = defineMachine({
    entity: 'email',
    initial: 'queued',
    table: {
        queued: { sent: 'sent', failed: 'failed', skip: 'skipped' },
        sent: {},
        // `skip` from `failed` as well as from `queued`. A mail the provider refused is
        // one the drain job will retry, and by the next attempt the reason not to send it
        // at all may have arrived — the client was archived in between. Without this edge
        // the retry raises `IllegalTransition` for ever and the row is `failed` until
        // somebody edits it by hand.
        failed: { requeue: 'queued', skip: 'skipped' },
        skipped: {},
    },
});
/**
 * `.docs/00-index.md` §6, package change proposals: "`proposed → approved |
 * declined`, applied by step 0 of the billing run." `RET-CHG-004` on
 * `client/requests.html` is the one in the mockups.
 */
export const retainerChangeMachine = defineMachine({
    entity: 'package change',
    initial: 'proposed',
    table: {
        proposed: { approve: 'approved', decline: 'declined' },
        approved: { apply: 'applied' },
        declined: {},
        applied: {},
    },
});
/**
 * `.docs/00-index.md` §6, content plans: "`draft → proposed → signed_off |
 * changes_requested`. No money. Owner/admin sign off." `PLAN-2026-09` is the one on
 * `client/requests.html`; `revise` takes a plan that came back with changes
 * requested to `draft`, where it is edited before being proposed again.
 */
export const contentPlanMachine = defineMachine({
    entity: 'content plan',
    initial: 'draft',
    table: {
        draft: { propose: 'proposed' },
        proposed: { sign_off: 'signed_off', request_changes: 'changes_requested' },
        signed_off: {},
        changes_requested: { revise: 'draft' },
    },
});
/**
 * `.docs/46-blog-and-forms.md` §4: "`draft → published`, `published → unpublished |
 * published` (update), `unpublished → published`, any non-archived `→ archived`,
 * `archived → draft` (restore)."
 *
 * **`scheduled` is not a state** (decision 8). A scheduled post is `published` with a
 * `published_at` still to come, and it becomes live when that instant passes with no
 * write in between — so the delivery API is right on the second rather than at the
 * next sweep. `update` is a publish over a post that is already published: a live
 * post's new revision, or a scheduled one's new time. Archiving a live post unpublishes
 * it on the way (`blog/service.ts` `archive` says so to the site's hooks), which is
 * why `published` may be archived directly. None is terminal: an archived post comes
 * back as a draft, because a post is never deleted and its addresses are kept for ever.
 */
export const blogPostMachine = defineMachine({
    entity: 'blog post',
    initial: 'draft',
    table: {
        draft: { publish: 'published', archive: 'archived' },
        published: { update: 'published', unpublish: 'unpublished', archive: 'archived' },
        unpublished: { publish: 'published', archive: 'archived' },
        archived: { restore: 'draft' },
    },
});
/* -------------------------------------------------------------------- everything */
/** Every machine, for the exhaustive table test and for anything enumerating them. */
export const MACHINES = [
    retainerMachine,
    workOrderMachine,
    invoiceMachine,
    taskMachine,
    widgetMachine,
    emailOutboxMachine,
    retainerChangeMachine,
    contentPlanMachine,
    blogPostMachine,
];
//# sourceMappingURL=states.js.map